import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Button, Dialog, Notice, TextField } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * ⚠ THE SENTINEL-SPLIT IDIOM, copied from `BoardPowerUpList.tsx:27-48`, whose
 * comment names this line. Do not re-decide it.
 *
 * The confirmation emphasises the workspace's name inside a sentence, wrapped
 * in a `<strong>`. Two fragment keys are not translatable — a join fixes word
 * order and the separator — and dropping the `<strong>` removes emphasis from a
 * DESTRUCTIVE confirmation. Taken: one whole-sentence key with a `{name}` slot,
 * resolved with a codepoint no translation can contain and split on it here.
 *
 * ⚠ THE FALLBACK ARM IS NOT OPTIONAL. A translation that omits `{name}` yields
 * one part, and rendering `parts[0]` alone would silently DELETE the workspace
 * name from a destructive dialog. The plain arm degrades to a correct sentence.
 */
const NAME_SENTINEL = String.fromCharCode(0);

export interface WorkspaceDeleteDialogProps {
  open: boolean;
  workspaceName: string;
  busy?: boolean;
  error?: string | null;
  onConfirm: (confirmName: string) => void;
  onCancel: () => void;
}

export function WorkspaceDeleteDialog({
  open,
  workspaceName,
  busy = false,
  error = null,
  onConfirm,
  onCancel,
}: WorkspaceDeleteDialogProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [confirmName, setConfirmName] = useState('');
  const canDelete = confirmName === workspaceName;

  useEffect(() => {
    if (!open) setConfirmName('');
  }, [open]);

  const bodyParts = t('workspaceSettings.deleteDialog.body', {
    name: NAME_SENTINEL,
  }).split(NAME_SENTINEL);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      title={t('workspaceSettings.deleteDialog.title')}
      description={t('workspaceSettings.deleteDialog.description')}
      size="sm"
      initialFocus={cancelRef}
      footer={
        <>
          <Button
            variant="danger"
            busy={busy}
            disabled={!canDelete}
            disabledReason={t('workspaceSettings.deleteDialog.confirmDisabled', {
              name: workspaceName,
            })}
            onClick={() => onConfirm(confirmName)}
          >
            {t('workspaceSettings.deleteDialog.confirm')}
          </Button>
          <Button ref={cancelRef} variant="quiet" onClick={onCancel}>
            {t('workspaceSettings.deleteDialog.cancel')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {bodyParts.length === 2 ? (
          <p className="text-sm text-ink-muted">
            {bodyParts[0]}
            <strong className="font-medium text-ink">{workspaceName}</strong>
            {bodyParts[1]}
          </p>
        ) : (
          <p className="text-sm text-ink-muted">
            {t('workspaceSettings.deleteDialog.body', { name: workspaceName })}
          </p>
        )}
        {error !== null ? <Notice tone="bad" body={error} /> : null}
        <TextField
          label={t('workspaceSettings.deleteDialog.nameLabel')}
          value={confirmName}
          onChange={(event) => setConfirmName(event.target.value)}
          autoComplete="off"
        />
      </div>
    </Dialog>
  );
}
